import React, { useState, useEffect } from 'react';
import '../../styles/pages/MCP/mcpConfigBox.scss';
import '../../styles/onboardingFlow/onboardingComponents/integrationModal.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Popup from '../../ui/popup/Popup';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import AINormalInput from '../../ui/inputs/AINormalInput';
import AISelectInput from '../../ui/inputs/AISelectInput';
import makeFirstLetterCapital from '../../lib/helper-functions/makeFirstLetterCapital';
import convertSnakeCaseToTitleCase from '../../lib/helper-functions/convertSnakeCaseToTitleCase';
import connect_mcp_api_call from '../../lib/api/mcp/connect-mcp';
import update_mcp_api_call from '../../lib/api/mcp/update-mcp';
import delete_mcp_api_call from '../../lib/api/mcp/delete-mcp';
import refresh_mcp_accounts_api_call from '../../lib/api/mcp/refresh-mcp-accounts';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import removeHttpPrefix from '../../lib/helper-functions/removeHttpPrefix';
import { submitLogToBugsnag } from '../../lib/api/log';
import store from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import {
  crossSVG,
  saveSVG,
  settingSVG,
  reloadArrowsSVG,
  warningWithCircleSVG,
} from '../../lib/allCustomSVGs';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  mcp: any;
  onUpdate: () => void;
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({
  isOpen,
  onClose,
  mcp,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editingMCP, setEditingMCP] = useState<any>(mcp);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshingAccounts, setIsRefreshingAccounts] = useState(false);
  const [manualAccountFields, setManualAccountFields] = useState<Set<string>>(
    new Set()
  );
  const [
    isActivationToggleConfirmationVisible,
    setIsActivationToggleConfirmationVisible,
  ] = useState(false);

  useEffect(() => {
    if (mcp) {
      // Initialize credentials object if it doesn't exist
      const initializedMCP = {
        ...mcp,
        credentials: mcp.credentials || {},
      };
      
      // If there are accounts with default values, set them in credentials
      if (mcp.credentials_fields && mcp.accounts) {
        mcp.credentials_fields.forEach((field: string) => {
          if (
            Array.isArray(mcp.accounts?.[field]) &&
            mcp.accounts[field].length > 0 &&
            !initializedMCP.credentials[field]
          ) {
            initializedMCP.credentials[field] =
              mcp.accounts[field][0]?.OptionValue || '';
          }
        });
      }
      
      setEditingMCP(initializedMCP);
      setManualAccountFields(new Set());
      setIsActivationToggleConfirmationVisible(false);
      setIsSubmitting(false);
      setIsDeleting(false);
    }
  }, [mcp]);

  const toggleEditPopupModal = (open: boolean) => {
    if (open === false) {
      setEditingMCP(undefined);
      setManualAccountFields(new Set());
    }
    setIsActivationToggleConfirmationVisible(false);
    setIsSubmitting(false);
    setIsDeleting(false);
    if (!open) {
      onClose();
    }
  };

  const constructDynamicOauthURL = (
    providerOauthURL: string,
    payload: object
  ) => {
    // @ts-ignore
    const STATE = btoa(JSON.stringify(payload));
    const REDIRECT_URI = `${window.location.origin}/onboarding`;
    const URL = `${providerOauthURL}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;
    return URL;
  };

  const oAuthRedirectHandler = () => {
    const mode = editingMCP?.is_active !== undefined ? 'update' : 'add';
    const isOauth =
      editingMCP?.oauth_url &&
      isNotEmptyString(editingMCP?.oauth_url) &&
      (mode === 'update' ? editingMCP?.oauth_required : true);

    if (isOauth) {
      try {
        setIsSubmitting(true);
        const redirectURL = constructDynamicOauthURL(
          editingMCP?.oauth_url,
          editingMCP
        );
        window.location.href = redirectURL;
      } catch (error) {
        setIsSubmitting(false);
        console.error('OAuth - Error connecting MCP:', error);
        submitLogToBugsnag(
          'error',
          `OAuth - Error in connecting MCP: ${error}`
        );
        store.dispatch(
          setMessage({
            message: `${t('MCP-OauthFailed')}`,
            messageType: 'error',
          })
        );
      }
    }
  };

  const onSubmitHandler = async (mcp: any) => {
    const mode = mcp?.is_active !== undefined ? 'update' : 'add';
    
    // Ensure credentials object exists
    const credentials = { ...(mcp.credentials || {}) };
    
    // For fields with accounts dropdowns, ensure the selected/default value is in credentials
    // This handles the case where user hasn't interacted with the dropdown but a default is shown
    if (mcp.credentials_fields && mcp.accounts) {
      mcp.credentials_fields.forEach((field: string) => {
        if (
          Array.isArray(mcp.accounts?.[field]) &&
          mcp.accounts[field].length > 0
        ) {
          // If field not in credentials, use the first account as default
          if (!credentials[field] || credentials[field] === '') {
            credentials[field] = mcp.accounts[field][0]?.OptionValue || '';
          }
        }
      });
    }
    
    const mcpWithCredentials = {
      ...mcp,
      credentials,
    };
    
    if (mode === 'add') {
      try {
        setIsSubmitting(true);
        const response = await connect_mcp_api_call(
          mcpWithCredentials.id,
          mcpWithCredentials.credentials
        );
        if (response?.success) {
          setIsSubmitting(false);
          if (response?.data?.is_valid) {
            onUpdate();
            toggleEditPopupModal(false);
          } else {
            onUpdate();
            setEditingMCP({
              ...mcpWithCredentials,
              connection_id: response?.data?.id,
              credentials: response?.data?.credentials,
              is_active: true,
              is_valid: false,
            });
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else if (response?.data && response?.data?.mcp_limit) {
          toggleEditPopupModal(false);
          store.dispatch(
            setMessage({
              message: `⚠️ ${t('MCP-LimitReached', {
                mcpLimit: response?.data?.mcp_limit,
              })}`,
              messageType: 'warning',
            })
          );
        } else {
          setIsSubmitting(false);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error connecting MCP:', error);
        submitLogToBugsnag('error', `Error in connecting MCP: ${error}`);
      }
    } else {
      try {
        setIsSubmitting(true);
        const response = await update_mcp_api_call(
          mcpWithCredentials.connection_id,
          mcpWithCredentials.credentials,
          mcpWithCredentials.is_active
        );
        if (response?.success) {
          setIsSubmitting(false);
          if (
            response?.data?.is_valid ||
            !mcpWithCredentials?.is_active ||
            mcpWithCredentials?.is_default
          ) {
            onUpdate();
            toggleEditPopupModal(false);
          } else {
            onUpdate();
            setEditingMCP({
              ...mcpWithCredentials,
              is_valid: false,
            });
            setIsActivationToggleConfirmationVisible(false);
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else if (response?.data && response?.data?.mcp_limit) {
          toggleEditPopupModal(false);
          store.dispatch(
            setMessage({
              message: `⚠️ ${t('MCP-LimitReached', {
                mcpLimit: response?.data?.mcp_limit,
              })}`,
              messageType: 'warning',
            })
          );
        } else {
          setIsSubmitting(false);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error updating MCP:', error);
        submitLogToBugsnag('error', `Error in updating MCP: ${error}`);
      }
    }
  };

  const deleteHandler = async (e: any) => {
    e.preventDefault();
    try {
      setIsDeleting(true);
      const response = await delete_mcp_api_call(editingMCP.connection_id);
      if (response) {
        onUpdate();
        toggleEditPopupModal(false);
      } else {
        throw new Error('Delete API failed');
      }
    } catch (error) {
      setIsDeleting(false);
      console.error('Error deleting MCP:', error);
      submitLogToBugsnag('error', `Error in deleting MCP: ${error}`);
    }
  };

  const handleRefreshAccounts = async (field: string) => {
    if (!editingMCP?.connection_id) return;

    try {
      setIsRefreshingAccounts(true);
      const response = await refresh_mcp_accounts_api_call(
        editingMCP.connection_id
      );

      if (response && response.accounts) {
        setEditingMCP({
          ...editingMCP,
          accounts: {
            ...editingMCP.accounts,
            [field]: response.accounts[field] || editingMCP.accounts[field],
          },
        });

        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshed')}`,
            messageType: 'success',
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshFailed')}`,
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error refreshing accounts:', error);
      store.dispatch(
        setMessage({
          message: `${t('MCP-AccountsRefreshFailed')}`,
          messageType: 'error',
        })
      );
    } finally {
      setIsRefreshingAccounts(false);
    }
  };


  return (
    <Popup
      styles={{ backgroundColor: '#fff' }}
      setTrigger={() => toggleEditPopupModal(false)}
      trigger={isOpen}
      additionalClassName='integrationModalPopup'
    >
      <form
        id='mcpModal-form-update'
        onSubmit={(e) => {
          e.preventDefault();
          onSubmitHandler(editingMCP);
        }}
        className='mcpConfigBox'
      >
        <div className='mcpConfigBox__title'>
          <div className='mcpConfigBox__title--main'>
            <div className='mcpConfigBox__title--main-image'>
              <img
                src={editingMCP?.logo}
                alt={makeFirstLetterCapital(editingMCP?.mcp_name)}
              />
            </div>
            <h1 className='mcpConfigBox__title--main-text'>
              {t('ManageMCP', {
                mcpName: makeFirstLetterCapital(editingMCP?.mcp_name),
              })}
            </h1>
          </div>

        </div>
        <div className='mcpConfigBox__content'>
          {(!editingMCP?.is_default && !editingMCP?.is_valid) ||
          (!editingMCP?.is_default &&
            editingMCP?.oauth_required &&
            isNotEmptyString(editingMCP?.oauth_url)) ? (
            <div className='integrationModalPopup__warning-box'>
              <div className='integrationModalPopup__warning-box-icon'>
                <CustomSVGs svg={warningWithCircleSVG} />
              </div>
              <p className='integrationModalPopup__warning-box-text'>
                {!editingMCP?.is_valid
                  ? t('MCP-InvalidCredentials')
                  : t('MCP-OauthConsentRequired')}
              </p>
            </div>
          ) : null}
          <div className='mcpConfigBox__content--inputs'>
            {editingMCP?.credentials_fields?.map(
              (field: string, index: number) => (
                <div
                  className='mcpConfigBox__content--inputs-field'
                  key={index}
                >
                  {editingMCP?.accounts &&
                  Array.isArray(editingMCP?.accounts?.[field]) &&
                  editingMCP?.accounts?.[field]?.length > 0 ? (
                    <>
                      <AISelectInput
                        defaultValue={
                          editingMCP?.credentials?.[field] ||
                          editingMCP?.accounts?.[field]?.[0]?.OptionValue ||
                          ''
                        }
                        setSelect={(value: string) => {
                          if (value === 'manual_account') {
                            setManualAccountFields(
                              (prev) => new Set(Array.from(prev).concat(field))
                            );
                          } else {
                            setManualAccountFields((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(field);
                              return newSet;
                            });
                          }
                          setEditingMCP({
                            ...editingMCP,
                            credentials: {
                              ...editingMCP?.credentials,
                              [field]:
                                field === 'MYSHOPIFY_DOMAIN'
                                  ? removeHttpPrefix(value)
                                  : value,
                            },
                          });
                        }}
                        selectOptionArr={[
                          ...editingMCP?.accounts?.[field].map(
                            (value: any) => ({
                              OptionValue: value?.OptionValue,
                              OptionName: `${value?.OptionName} ${
                                value?.OptionValue !== value?.OptionName
                                  ? `(${value?.OptionValue})`
                                  : ''
                              }`,
                            })
                          ),
                          {
                            OptionValue: 'manual_account',
                            OptionName: t('MCP-ManualAccount', {
                              credentialFieldName:
                                convertSnakeCaseToTitleCase(field),
                            }),
                          },
                        ]}
                        placeholder={convertSnakeCaseToTitleCase(field)}
                        id={`${field}-input`}
                        refreshButton={{
                          onClick: () => handleRefreshAccounts(field),
                          isLoading: isRefreshingAccounts,
                          icon: reloadArrowsSVG,
                        }}
                      />
                      {manualAccountFields.has(field) && (
                        <AINormalInput
                          key={`${field}-manual`}
                          changeListeners={[
                            (event) => {
                              setEditingMCP({
                                ...editingMCP,
                                credentials: {
                                  ...editingMCP?.credentials,
                                  [`${field}_manual`]:
                                    field === 'MYSHOPIFY_DOMAIN'
                                      ? removeHttpPrefix(event.target.value)
                                      : event.target.value,
                                },
                              });
                            },
                          ]}
                          id={`${field}-manual-input`}
                          value={
                            editingMCP?.credentials?.[`${field}_manual`] || ''
                          }
                          required={true}
                          innerPlaceholder={
                            t(`EnterYour`) +
                            ' ' +
                            convertSnakeCaseToTitleCase(field) +
                            (field === 'MYSHOPIFY_DOMAIN'
                              ? ' ("example.myshopify.com")'
                              : '')
                          }
                          pattern={
                            (field === 'MANAGER_ACCOUNT_ID' ||
                              field === 'ACCOUNT_ID') &&
                            editingMCP?.mcp_slug === 'google_ads'
                              ? '^[0-9]+$'
                              : undefined
                          }
                          title={
                            (field === 'MANAGER_ACCOUNT_ID' ||
                              field === 'ACCOUNT_ID') &&
                            editingMCP?.mcp_slug === 'google_ads'
                              ? t('OnlyNumeric-validation')
                              : undefined
                          }
                        />
                      )}
                    </>
                  ) : field === 'MANAGER_ACCOUNT_ID' &&
                    editingMCP?.mcp_slug === 'google_ads' &&
                    editingMCP?.accounts &&
                    Array.isArray(editingMCP?.accounts?.['ACCOUNT_ID']) &&
                    editingMCP?.accounts?.['ACCOUNT_ID']?.length > 0 &&
                    editingMCP?.credentials?.['ACCOUNT_ID'] !==
                      'manual_account' ? null : (
                    <AINormalInput
                      key={index}
                      changeListeners={[
                        (event) => {
                          setEditingMCP({
                            ...editingMCP,
                            credentials: {
                              ...editingMCP?.credentials,
                              [field]:
                                field === 'MYSHOPIFY_DOMAIN'
                                  ? removeHttpPrefix(event.target.value)
                                  : event.target.value,
                            },
                          });
                        },
                      ]}
                      id={`${field}-input`}
                      value={editingMCP?.credentials?.[field] || ''}
                      placeholder={
                        field === 'MANAGER_ACCOUNT_ID' &&
                        editingMCP?.mcp_slug === 'google_ads'
                          ? `${convertSnakeCaseToTitleCase(field)} (${t(
                              'Optional'
                            )})`
                          : convertSnakeCaseToTitleCase(field)
                      }
                      required={
                        !(
                          field === 'MANAGER_ACCOUNT_ID' &&
                          editingMCP?.mcp_slug === 'google_ads'
                        )
                      }
                      innerPlaceholder={
                        t(`EnterYour`) +
                        ' ' +
                        convertSnakeCaseToTitleCase(field) +
                        (field === 'MYSHOPIFY_DOMAIN'
                          ? ' ("example.myshopify.com")'
                          : '')
                      }
                      pattern={
                        (field === 'MANAGER_ACCOUNT_ID' ||
                          field === 'ACCOUNT_ID') &&
                        editingMCP?.mcp_slug === 'google_ads'
                          ? '^[0-9]+$'
                          : undefined
                      }
                      title={
                        (field === 'MANAGER_ACCOUNT_ID' ||
                          field === 'ACCOUNT_ID') &&
                        editingMCP?.mcp_slug === 'google_ads'
                          ? t('OnlyNumeric-validation')
                          : undefined
                      }
                    />
                  )}
                </div>
              )
            )}
          </div>
          <div className='mcpConfigBox__content--info'>
            <p
              style={{
                marginTop: '1rem',
                fontSize: '1rem',
                fontStyle: 'italic',
                color: '#7a7a7a',
              }}
            >
              {isActivationToggleConfirmationVisible
                ? ' * ' + t('MCP-ActivationToggleConfirmation')
                : ''}
            </p>
          </div>
        </div>
        <div className='mcpConfigBox__buttons'>
          {editingMCP?.is_active !== undefined ? (
            <>
              {!editingMCP?.is_default && (
                <MainSquareButton
                  value={
                    isDeleting ? `${t('Disconnecting')}...` : t('Disconnect')
                  }
                  disabled={isSubmitting || isDeleting}
                  type='button'
                  form='mcpModal-form-update'
                  onClick={deleteHandler}
                  classes='mcpConfigBox__buttons--danger'
                  buttonType='delete'
                  icon={crossSVG}
                />
              )}
              <MainSquareButton
                value={
                  isSubmitting
                    ? `${t('Updating')}...`
                    : !editingMCP?.is_default &&
                      editingMCP?.oauth_required &&
                      isNotEmptyString(editingMCP?.oauth_url)
                    ? t('Authorize')
                    : t('UpdateMCP')
                }
                disabled={isSubmitting || isDeleting}
                type={
                  !editingMCP?.is_default &&
                  editingMCP?.oauth_required &&
                  isNotEmptyString(editingMCP?.oauth_url)
                    ? 'button'
                    : 'submit'
                }
                form='mcpModal-form-update'
                onClick={
                  !editingMCP?.is_default &&
                  editingMCP?.oauth_required &&
                  isNotEmptyString(editingMCP?.oauth_url)
                    ? oAuthRedirectHandler
                    : undefined
                }
                classes='mcpConfigBox__buttons--update'
                icon={settingSVG}
              />
            </>
          ) : (
            <>
              <MainSquareButton
                value={t('Close')}
                type='button'
                onClick={() => toggleEditPopupModal(false)}
                classes='mcpConfigBox__buttons--close'
                buttonType='light'
              />
              <MainSquareButton
                value={isSubmitting ? `${t('Connecting')}...` : t('ConnectMCP')}
                disabled={isSubmitting || isDeleting}
                type='submit'
                classes='mcpConfigBox__buttons--update'
                form='mcpModal-form-update'
                icon={saveSVG}
              />
            </>
          )}
        </div>
      </form>
    </Popup>
  );
};

export default IntegrationModal;
